import { FC, useContext, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  createResponseContext,
  getWebSocketUrl,
  initialQueryResponse,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { useQueryRequest } from './QueryRequestProvider'
import { useModal } from '../../../../../components'
import { getInterconsultasEquipe, getInterconsultasSolicitante } from './_requests'
import { useEscopoPareceres } from './escopoPareceres'
import { InterconsultaSolicitanteList } from './_models'
import { Stomp } from '@stomp/stompjs'
import { useAuth } from '../../../../auth'

const SOCKET_URL = getWebSocketUrl('/socket/atendimento')

const QueryResponseContext =
  createResponseContext<InterconsultaSolicitanteList>(initialQueryResponse)
export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { isOpen } = useModal()
  const { state } = useQueryRequest()
  const escopo = useEscopoPareceres()
  const { auth } = useAuth()
  const queryClient = useQueryClient()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries([QUERIES.PARECERES_LIST])
  }, [queryClient])

  useEffect(() => {
    if (!auth?.accessToken) return

    const stompClient = Stomp.client(SOCKET_URL)

    const onConnect = () => {
      stompClient.subscribe('/topic/atendimento', () => {
        invalidateQuery()
      })
    }

    stompClient.connect({}, onConnect)

    return () => {
      stompClient.disconnect(() => console.log('Disconnected'))
    }
  }, [auth?.accessToken, invalidateQuery])

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery(
    [QUERIES.PARECERES_LIST, escopo, query, isOpen],
    () =>
      escopo === 'equipe' ? getInterconsultasEquipe(query) : getInterconsultasSolicitante(query),
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !isOpen,
    }
  )

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, isError, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
